/**
 * IndexedDB Article Repository Implementation
 *
 * Implements IArticleRepository using Dexie.js for IndexedDB storage.
 * Supports multiple article types and department-based access control.
 */

import { db } from '../../db';
import type { IArticleRepository, ArticleFilterOptions } from '../interfaces/IArticleRepository';
import type {
  Article,
  ArticleCategory,
  ArticleType,
  ArticleStep,
  ArticleContent,
  Department,
  HowToContent,
  KnownErrorContent,
  SOPRunbookContent,
  EmergencyContact,
} from '../../../types/knowledgeBase';
import { Department as DepartmentEnum, ArticleType as ArticleTypeEnum } from '../../../types/knowledgeBase';
import type { CreateArticleDTO, UpdateArticleDTO } from '../../../types/dto';

/**
 * Transform form step data to article steps with IDs
 */
function transformSteps(
  steps: Array<{
    id?: string;
    title: string;
    content: string;
    imageUrl?: string;
    imageAlt?: string;
  }>
): ArticleStep[] {
  return steps.map((step, index) => ({
    id: step.id ?? crypto.randomUUID(),
    stepNumber: index + 1,
    title: step.title,
    content: step.content,
    imageUrl: step.imageUrl,
    imageAlt: step.imageAlt,
  }));
}

/**
 * Transform form emergency contacts to entity contacts with IDs
 */
function transformEmergencyContacts(
  contacts: Array<{
    id?: string;
    name: string;
    role: string;
    contact: string;
  }>
): EmergencyContact[] {
  return contacts.map((c) => ({
    id: c.id ?? crypto.randomUUID(),
    name: c.name,
    role: c.role,
    contact: c.contact,
  }));
}

/**
 * Transform form content data to article content with IDs
 */
function transformArticleContent(
  formContent: CreateArticleDTO['articleContent']
): ArticleContent {
  switch (formContent.type) {
    case 'how-to':
      return {
        type: 'how-to',
        content: {
          steps: transformSteps(formContent.content.steps),
        } as HowToContent,
      };
    case 'known-error':
      return {
        type: 'known-error',
        content: {
          affectedSystems: formContent.content.affectedSystems,
          symptoms: formContent.content.symptoms,
          workaround: formContent.content.workaround,
          relatedArticleIds: formContent.content.relatedArticleIds,
        } as KnownErrorContent,
      };
    case 'sop-runbook':
      return {
        type: 'sop-runbook',
        content: {
          objective: formContent.content.objective,
          prerequisites: formContent.content.prerequisites,
          procedureSteps: transformSteps(formContent.content.procedureSteps),
          verification: formContent.content.verification,
          rollbackProcedure: formContent.content.rollbackProcedure,
          emergencyContacts: transformEmergencyContacts(formContent.content.emergencyContacts),
        } as SOPRunbookContent,
      };
  }
}

/**
 * Check if user has access to article based on department
 */
function hasAccess(article: Article, userDepartment?: Department, isAdmin?: boolean): boolean {
  if (isAdmin) return true;
  if (!userDepartment) return true;
  if (article.audiences.includes(DepartmentEnum.ALL)) return true;
  return article.audiences.includes(userDepartment);
}

/**
 * Search text content for a query
 */
function searchInText(text: string, query: string): boolean {
  return text.toLowerCase().includes(query);
}

/**
 * Search article content based on type
 */
function searchArticleContent(article: Article, query: string): boolean {
  const content = article.articleContent;

  switch (content.type) {
    case 'how-to':
      return content.content.steps.some(
        (step) => searchInText(step.title, query) || searchInText(step.content, query)
      );
    case 'known-error':
      return (
        content.content.affectedSystems.some((s) => searchInText(s, query)) ||
        searchInText(content.content.symptoms, query) ||
        searchInText(content.content.workaround, query)
      );
    case 'sop-runbook':
      return (
        searchInText(content.content.objective, query) ||
        content.content.prerequisites.some((p) => searchInText(p, query)) ||
        searchInText(content.content.verification, query) ||
        searchInText(content.content.rollbackProcedure, query) ||
        content.content.procedureSteps.some(
          (step) => searchInText(step.title, query) || searchInText(step.content, query)
        )
      );
  }
}

export class IndexedDBArticleRepository implements IArticleRepository {
  async getAll(): Promise<Article[]> {
    return db.articles.toArray();
  }

  async getById(id: string): Promise<Article | null> {
    const article = await db.articles.get(id);
    return article ?? null;
  }

  async create(data: CreateArticleDTO): Promise<Article> {
    const now = new Date();

    // Transform form content to article content
    const articleContent = transformArticleContent(data.articleContent);

    // For backward compatibility, extract steps from how-to content
    const steps: ArticleStep[] =
      articleContent.type === 'how-to' ? articleContent.content.steps : [];

    const article: Article = {
      id: crypto.randomUUID(),
      title: data.title,
      description: data.description,
      category: data.category,
      tags: data.tags,
      articleType: data.articleType,
      articleContent,
      audiences: data.audiences,
      steps,
      authorId: data.authorId,
      authorName: data.authorName,
      createdAt: now,
      updatedAt: now,
      viewCount: 0,
      isPublished: data.isPublished,
    };

    await db.articles.add(article);
    return article;
  }

  async update(id: string, data: UpdateArticleDTO): Promise<Article | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    // Transform article content if provided
    let articleContent = existing.articleContent;
    let steps = existing.steps;

    if (data.articleContent) {
      articleContent = transformArticleContent(data.articleContent);
      // Update steps for backward compatibility
      steps = articleContent.type === 'how-to' ? articleContent.content.steps : [];
    }

    const updatedArticle: Article = {
      ...existing,
      title: data.title ?? existing.title,
      description: data.description ?? existing.description,
      category: data.category ?? existing.category,
      tags: data.tags ?? existing.tags,
      articleContent,
      audiences: data.audiences ?? existing.audiences,
      steps,
      isPublished: data.isPublished ?? existing.isPublished,
      updatedAt: new Date(),
    };

    await db.articles.put(updatedArticle);
    return updatedArticle;
  }

  async delete(id: string): Promise<boolean> {
    const existing = await this.getById(id);
    if (!existing) {
      return false;
    }
    await db.articles.delete(id);
    return true;
  }

  async getPublished(): Promise<Article[]> {
    const all = await this.getAll();
    return all.filter((a) => a.isPublished);
  }

  async getByCategory(category: ArticleCategory): Promise<Article[]> {
    return db.articles.where('category').equals(category).toArray();
  }

  async getByType(articleType: ArticleType): Promise<Article[]> {
    return db.articles.where('articleType').equals(articleType).toArray();
  }

  async getByAudience(department: Department): Promise<Article[]> {
    const all = await this.getAll();
    return all.filter(
      (a) => a.audiences.includes(DepartmentEnum.ALL) || a.audiences.includes(department)
    );
  }

  async getByAuthor(authorId: string): Promise<Article[]> {
    return db.articles.where('authorId').equals(authorId).toArray();
  }

  async getRelatedArticles(articleIds: string[]): Promise<Article[]> {
    if (articleIds.length === 0) {
      return [];
    }
    const articles = await Promise.all(articleIds.map((id) => this.getById(id)));
    return articles.filter((a): a is Article => a !== null);
  }

  async search(query: string): Promise<Article[]> {
    if (!query.trim()) {
      return this.getPublished();
    }

    const lowerQuery = query.toLowerCase();
    const published = await this.getPublished();

    return published.filter(
      (article) =>
        searchInText(article.title, lowerQuery) ||
        searchInText(article.description, lowerQuery) ||
        article.tags.some((tag) => searchInText(tag, lowerQuery)) ||
        searchArticleContent(article, lowerQuery)
    );
  }

  async incrementViewCount(id: string): Promise<Article | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    const updatedArticle: Article = {
      ...existing,
      viewCount: existing.viewCount + 1,
    };

    await db.articles.put(updatedArticle);
    return updatedArticle;
  }

  async updatePublishStatus(id: string, isPublished: boolean): Promise<Article | null> {
    return this.update(id, { isPublished });
  }

  async getFiltered(filters: ArticleFilterOptions): Promise<Article[]> {
    let articles = await this.getAll();

    // Apply access control filter first
    if (!filters.isAdmin && filters.userDepartment) {
      articles = articles.filter((a) => hasAccess(a, filters.userDepartment, filters.isAdmin));
    }

    // Filter by publish status
    if (filters.isPublished !== undefined) {
      articles = articles.filter((a) => a.isPublished === filters.isPublished);
    }

    // Filter by categories
    if (filters.categories && filters.categories.length > 0) {
      articles = articles.filter((a) => filters.categories!.includes(a.category));
    }

    // Filter by article types
    if (filters.articleTypes && filters.articleTypes.length > 0) {
      articles = articles.filter((a) => filters.articleTypes!.includes(a.articleType));
    }

    // Filter by audiences (for admin filtering, not access control)
    if (filters.audiences && filters.audiences.length > 0) {
      articles = articles.filter((a) =>
        filters.audiences!.some(
          (audience) =>
            a.audiences.includes(audience) ||
            (audience !== DepartmentEnum.ALL && a.audiences.includes(DepartmentEnum.ALL))
        )
      );
    }

    // Filter by author
    if (filters.authorId) {
      articles = articles.filter((a) => a.authorId === filters.authorId);
    }

    // Search filter
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase();
      articles = articles.filter(
        (a) =>
          searchInText(a.title, query) ||
          searchInText(a.description, query) ||
          a.tags.some((tag) => searchInText(tag, query)) ||
          searchArticleContent(a, query)
      );
    }

    return articles;
  }
}
